import multiNotePlayer from './multiNotePlayer';
import eqProcessor from './eqProcessor';
import { playChordInstrument, stopAllInstruments, warmUpInstrument } from './chordInstruments';
import { REVERB_DECAY, REVERB_MIX } from './audioSettings';
import { getScaleVoicingNotes } from './musicTheory';
import { getSetting, setSetting, subscribeUser } from '../data/userStore';
import { isLightDevice } from './devicePerformance';
import { isAndroid } from './platformUtils';
import { ANDROID_LATENCY_HINT } from './androidToneContext';

/**
 * AudioEngine - Unified audio playback using multiNotePlayer (WAV files)
 *
 * All platforms (iOS, Android, Web) now use the same engine for consistent audio quality.
 * Removed legacy Tone.js synthesis code in favor of high-quality pre-rendered WAV files.
 */

// How far ahead a hand-played chord aims, so every layer can hit the same instant
// instead of starting whenever it happens to get there (see playChord). Small
// enough to stay imperceptible on a tap, and unrelated to the Transport's own
// lookahead, so scheduled playback and Bluetooth latency are untouched.
const MANUAL_TRIGGER_LEAD = 0.02;

// Android demande un tampon de sortie bien plus large (ANDROID_LATENCY_HINT, cf
// androidToneContext) pour ne pas crachotter. Le currentTime lu depuis le thread
// principal y est donc perime de presque tout un tampon, et une cible a 20 ms
// tombe dans une zone deja rendue : les deux couches perdent leur rendez-vous et
// repartent chacune "des que possible", ce qui s'entend comme un flam au tap
// (le piano attaque net, la nappe suit). Une progression n'a jamais eu le
// probleme, le Transport fournit son instant 110 a 180 ms a l'avance, donc bien
// au-dela du tampon. On vise ici le tampon plus une marge, pour que la cible
// soit encore dans le futur quand la couche la plus lente y arrive.
const ANDROID_TRIGGER_MARGIN = 0.02;
const ANDROID_MANUAL_TRIGGER_LEAD = ANDROID_LATENCY_HINT + ANDROID_TRIGGER_MARGIN;

// Lu a l'usage et non fige a l'import : la plateforme passe par Capacitor, qui
// n'est pas garanti pret quand ce module est evalue.
const manualTriggerLead = () => (isAndroid() ? ANDROID_MANUAL_TRIGGER_LEAD : MANUAL_TRIGGER_LEAD);
class AudioEngine {
  constructor() {
    this.initialized = false;
    this.currentChord = null;
    this.currentScaleRoot = null;
    this.currentScaleMode = null;
    this.volume = -6; // Default volume in dB
    this.playHighOctave = true;
    // When true, the bass drone stays on the scale tonic (pedal point) while the
    // voicings move above it. When false, the bass follows each chord's own root.
    this.staticBass = true;
    // When true, a low drone bass sounds under each voicing. When false, only the
    // chord tones in the register window play (no held bass).
    this.drone = true;
    // La reverb n'appartient plus a personne : une seule pour toute l'app, dans
    // reverbBus. Ces deux valeurs ne sont que le miroir des reglages du mixer.
    this.reverbMix = REVERB_MIX; // Default reverb wet mix
    this.reverbDecay = REVERB_DECAY; // Duree commune (cf reverbBus)
    // Chord instrument keys: 'default' (bare WAV drone, shown as "Pad"), 'piano',
    // 'guitar', or 'lush' (drone layered with piano, shown as "Default"). 'lush'
    // is the startup default, so a fresh user hears the richer "Default" sound.
    // Read live from the user mirror (never captured here: the singleton is built
    // before the mirror is hydrated).
    const startup = this.getInstrument();
    if (startup !== 'default') warmUpInstrument(startup);
    // Le prechauffage ci-dessus tombe AVANT l'hydratation du miroir user : il ne
    // peut voir que le defaut, donc il chauffait 'lush' meme chez quelqu'un qui a
    // choisi 'guitar', dont la banque ne partait qu'au premier accord. On refait
    // le geste a chaque notification du miroir, l'hydratation comprise, pour que
    // la banque REELLEMENT choisie soit prete avant usage. Sans desabonnement :
    // le moteur est un singleton, `warmUpInstrument` est idempotent (la banque
    // est memoisee) et 'default' ne charge rien.
    subscribeUser(() => warmUpInstrument(this.getInstrument()));
  }

  // Live read from the user mirror at usage time.
  getInstrument() {
    const saved = getSetting('upScale.chordInstrument', null);
    if (['default', 'piano', 'guitar', 'lush'].includes(saved)) return saved;
    // Aucun choix explicite. Le defaut reste 'lush' (nappe + piano superposes),
    // sauf sur un appareil Android juge trop juste : la superposition y fait
    // deborder le rendu audio et crachotter le son, on retombe alors sur la
    // nappe seule. L'utilisateur garde la main, un choix dans les reglages est
    // respecte ici et n'est jamais reecrit.
    return isLightDevice() ? 'default' : 'lush';
  }

  // Switch the chord instrument. Stops any sounding chord so the previous timbre
  // does not linger, then warms up the new instrument's sample bank.
  setInstrument(name) {
    const next = ['default', 'piano', 'guitar', 'lush'].includes(name) ? name : 'default';
    setSetting('upScale.chordInstrument', next);
    this.stopAllSounds();
    warmUpInstrument(next);
  }

  /**
   * Initialize the audio engine
   */
  async initialize() {
    if (this.initialized) {
      return;
    }

    try {
      await multiNotePlayer.initialize();

      this.initialized = true;
    } catch (error) {
      console.error('[AudioEngine] Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Play a chord with given root note and quality
   * @param {string} rootNote - Root note (C, D, E, F, G, A, B, C#, D#, etc.)
   * @param {string} quality - Chord quality (Major, Minor, Dominant7, etc.)
   * @param {number} time - Optional time to play (for scheduling)
   */
  async playChord(rootNote, quality, time = null, scaleRoot = null) {
    try {
      if (!this.initialized) {
        await this.initialize();
      }

      // Static bass: hold the scale tonic under every voicing (pedal point).
      // Moving bass: let the bass track the chord being played.
      const root = this.staticBass ? (scaleRoot || this.currentScaleRoot) : rootNote;
      // Play the exact voicing notes shown on the pad, stacked from within the
      // scale, on every scale and not just the pentatonics: rebuilding a chord
      // from its name alone made 11 scales sound notes absent from the fretboard.
      // Skip in drone mode (single note).
      const explicitNotes = quality !== 'drone'
        ? getScaleVoicingNotes(scaleRoot || this.currentScaleRoot, this.currentScaleMode, rootNote, quality)
        : null;
      const inst = this.getInstrument();

      // A manual tap used to leave each layer reading the clock on its own, and
      // the sampler read it only once the pad had finished its whole voice-leading
      // pass: measured on device, the piano landed ~28ms behind the pad (median of
      // 125 taps, up to 45ms), which is heard as a flam. Both layers now aim at one
      // shared instant, just far enough ahead to still be in the future when the
      // slower one gets there, and they start in parallel so that lead stays small.
      // A progression already carries its own instant from the Transport, which is
      // why playback never had the problem.
      const ctx = multiNotePlayer.audioContext;
      const target = time !== null
        ? time
        : (ctx ? ctx.currentTime + manualTriggerLead() : null);

      const layers = [];
      // Default WAV drone engine for 'default', and the base layer of 'lush'.
      if (inst === 'default' || inst === 'lush') {
        layers.push(multiNotePlayer.playChord(rootNote, quality, target, root, explicitNotes, this.drone));
      }
      // Sampler instruments (piano / guitar, plus the piano layer of 'lush').
      if (inst !== 'default') {
        layers.push(playChordInstrument(inst, rootNote, quality, root, explicitNotes, this.drone, target));
      }
      await Promise.all(layers);
      this.currentChord = `${rootNote}-${quality}`;
    } catch (error) {
      console.error('[AudioEngine] Failed to play chord:', error);
      throw error;
    }
  }

  /**
   * Stop all currently playing audio
   */
  stopAllSounds() {
    try {
      multiNotePlayer.stopAll();
      stopAllInstruments();
      this.currentChord = null;
    } catch (error) {
      console.error('[AudioEngine] Failed to stop sounds:', error);
    }
  }

  /**
   * Stop chord (alias for stopAllSounds for backward compatibility)
   */
  stopChord() {
    this.stopAllSounds();
  }

  /**
   * Switch to a new chord (alias for playChord for backward compatibility)
   * @param {string} rootNote - Root note
   * @param {string} quality - Chord quality
   * @param {number} time - Optional time to play
   */
  async switchChord(rootNote, quality, time = null, scaleRoot = null) {
    return this.playChord(rootNote, quality, time, scaleRoot);
  }

  /**
   * Set the current scale context
   * @param {string} rootNote - Root note
   * @param {string} modeName - Mode name (Ionian, Dorian, etc.)
   * @param {Function} onProgress - Progress callback
   */
  async setCurrentScale(rootNote, modeName, onProgress = null) {
    this.currentScaleRoot = rootNote;
    this.currentScaleMode = modeName;

    // No pre-rendering needed with multiNotePlayer
    if (onProgress) {
      onProgress(100, 1, 1); // Instant completion
    }
  }

  /**
   * Set master volume
   * @param {number} db - Volume in decibels
   */
  setVolume(db) {
    this.volume = db;
    multiNotePlayer.setVolume(db);
  }

  /**
   * Set the gain for a chord degree ('drone', 'root', 'third', 'fifth', 'seventh')
   * @param {string} degree - Degree key
   * @param {number} gain - Linear gain (0 = muted)
   */
  setDegreeVolume(degree, gain) {
    multiNotePlayer.setDegreeVolume(degree, gain);
  }

  /**
   * Enable/disable high octave playback
   * @param {boolean} enabled
   */
  setPlayHighOctave(enabled) {
    this.playHighOctave = enabled;
    multiNotePlayer.setPlayHighOctave(enabled);
  }

  /**
   * Toggle the static bass (pedal point). When enabled the bass drone stays on
   * the scale tonic; when disabled it follows each chord's own root.
   * @param {boolean} enabled
   */
  setStaticBass(enabled) {
    this.staticBass = enabled;
  }

  /**
   * Toggle the low drone bass. When enabled a held bass note sounds an octave
   * below each voicing; when disabled only the chord tones in the register
   * window play.
   * @param {boolean} enabled
   */
  setDrone(enabled) {
    this.drone = enabled;
  }

  /**
   * Set whether to use pre-rendering (no-op, kept for API compatibility)
   * @param {boolean} enabled
   */
  setUsePreRendering(enabled) {
    // No-op: WAV player doesn't need pre-rendering
  }

  /**
   * Set chord duration (no-op, kept for API compatibility)
   * @param {number} duration - Duration in seconds
   */
  setChordDuration(duration) {
    // No-op: the note samples loop indefinitely, a chord lasts until it is stopped
  }

  /**
   * Global EQ - frequencies of the 8 bands (Hz)
   * @returns {number[]}
   */
  getEqFrequencies() {
    return eqProcessor.getFrequencies();
  }

  /**
   * Global EQ - current band gains in dB
   * @returns {number[]}
   */
  getEqGains() {
    return eqProcessor.getGains();
  }

  /**
   * Set the gain of a global EQ band
   * @param {number} index - Band index (0-7)
   * @param {number} db - Gain in decibels
   */
  setEqBandGain(index, db) {
    eqProcessor.setBandGain(index, db);
  }

  /**
   * Reset all global EQ bands to 0 dB (flat)
   */
  resetEq() {
    eqProcessor.reset();
  }

  /**
   * Set reverb wet mix
   * @param {number} mix - Wet mix amount (0-1)
   */
  setReverbMix(mix) {
    this.reverbMix = mix;
    multiNotePlayer.setReverbMix(mix);
  }

  /**
   * Set reverb decay time
   * @param {number} decay - Decay time in seconds
   */
  setReverbDecay(decay) {
    this.reverbDecay = decay;
    multiNotePlayer.setReverbDecay(decay);
  }

  /**
   * Get current rendering status
   * @returns {Object} Status object
   */
  getPreRenderingStatus() {
    return {
      isPreRendering: false,
      progress: 100,
      message: 'Using live WAV playback - no pre-rendering needed'
    };
  }

  /**
   * Cleanup and dispose resources
   */
  dispose() {
    try {
      multiNotePlayer.dispose();
      this.initialized = false;
    } catch (error) {
      console.error('[AudioEngine] Failed to dispose:', error);
    }
  }
}

export default new AudioEngine();
